import { SearchBondsPayload } from '../../../hooks/documents/useFetchBonds';
import { SearchLoansPayload } from '../../../hooks/documents/useFetchLoans';
import { DEFAULT_DOCUMENTS_TAKE } from '../../../hooks/documents/utils/fetchAllDocuments';
import { CompanyFilingsDto } from '../../../services/dtos/companyFilings/companyFilings.dto';
import { SearchDocumentsDto } from '../../../services/dtos/securities/searchDocuments.dto';
import { BondsSortOption, LoansSortOption } from '../../../services/securities/securities.constants';
import BondAmountRenderer from './cellRenderers/BondAmountRenderer';
import BondSecurityRenderer from './cellRenderers/BondSecurityRenderer';
import DisabledReportLinkRenderer from './cellRenderers/DisabledReportLinkRenderer';
import DocumentDateRenderer from './cellRenderers/DocumentDateRenderer';
import DocumentNameRenderer from './cellRenderers/DocumentNameRenderer';
import IssueOrDueDateRenderer from './cellRenderers/IssueOrDueDateRenderer';
import IssuerNameRenderer from './cellRenderers/IssuerNameRenderer';
import LoanAmountRenderer from './cellRenderers/LoanAmountRenderer';
import LoanSecurityRenderer from './cellRenderers/LoanSecurityRenderer';
import LockedDocumentRenderer from './cellRenderers/LockedDocumentRenderer';
import CompanyFilingsFileNameRenderer from './cellRenderers/CompanyFilingsFileNameRenderer';
import CompanyDocumentTypeRenderer from './cellRenderers/CompanyFilingsTypeRenderer';
import { BASE_ARTICLE_ID } from './CompanyDocumentsTab.constants';
import DateFilterRenderer from './filterRenderers/DateFilterRenderer';
import FilingsTypeFilterRenderer from './filterRenderers/FilingsTypeFilterRenderer';
import StringFilterRenderer from './filterRenderers/StringFilterRenderer';
import { TableConfiguration } from './GroupedTableRow/GroupedTableRow.types';
import LoanBondDateRenderer from './cellRenderers/LoanBondDateRenderer';

export const getDefaultFetchBondsPayload = (issuerId: string, take?: number): SearchBondsPayload => ({
  ...getBaseDocumentsPayload(issuerId, take),
  sort: BondsSortOption.document_date,
});

export const getDefaultFetchLoansPayload = (issuerId: string, take?: number): SearchLoansPayload => ({
  ...getBaseDocumentsPayload(issuerId, take),
  sort: LoansSortOption.document_date,
});

const getBaseDocumentsPayload = (issuerId: string, take?: number) => ({
  take: take ?? DEFAULT_DOCUMENTS_TAKE,
  skip: 0,
  sortDesc: true,
  filters: {
    cs_companyid: {
      values: [Number(issuerId)],
    },
  },
});

const dateColumnWidth = '12rem';
const issuerColumnWidth = '29rem';
const amountColumnWidth = '16rem';

export const LOANS_TABLE_CONFIG: TableConfiguration<SearchDocumentsDto> = {
  columns: [
    {
      dataKey: 'borrower_legal_name',
      header: 'Borrower',
      cellRenderer: IssuerNameRenderer,
      cellRendererProps: { hideInSubgroup: true },
      width: issuerColumnWidth,
    },
    {
      dataKey: 'issue_amount',
      header: 'Amount',
      cellRenderer: LoanAmountRenderer,
      width: amountColumnWidth,
      align: 'right',
    },
    {
      dataKey: 'report_title',
      header: 'Loan/Security',
      cellRenderer: LoanSecurityRenderer,
      cellLinkRenderer: DisabledReportLinkRenderer,
    },
    {
      dataKey: 'launch_date',
      header: 'Issue date',
      cellRenderer: IssueOrDueDateRenderer,
      cellRendererProps: { hideInSubgroup: true },
      width: dateColumnWidth,
    },
    { dataKey: 'document_name', header: 'Document', cellRenderer: DocumentNameRenderer },
    { dataKey: 'document_date', header: 'Document date', cellRenderer: LoanBondDateRenderer, width: dateColumnWidth },
  ],
  getRowKey: (document) => document.id,
  getIsDisabled: (data) => !data.can_access,
  disabledItemElement: LockedDocumentRenderer,
  isSortable: true,
};

export const BONDS_TABLE_CONFIG: TableConfiguration<SearchDocumentsDto> = {
  columns: [
    {
      dataKey: 'issuer_legal_name',
      header: 'Issuer',
      cellRenderer: IssuerNameRenderer,
      cellRendererProps: { hideInSubgroup: true },
      width: issuerColumnWidth,
    },
    {
      dataKey: 'issue_amount',
      header: 'Amount',
      cellRenderer: BondAmountRenderer,
      width: amountColumnWidth,
      align: 'right',
    },
    {
      dataKey: 'report_title',
      header: 'Bond/Security',
      cellRenderer: BondSecurityRenderer,
    },
    {
      dataKey: 'maturity_date',
      header: 'Due date',
      cellRenderer: IssueOrDueDateRenderer,
      cellRendererProps: { hideInSubgroup: true },
      width: dateColumnWidth,
    },
    { dataKey: 'document_name', header: 'Document', cellRenderer: DocumentNameRenderer },
    { dataKey: 'document_date', header: 'Document date', cellRenderer: LoanBondDateRenderer, width: dateColumnWidth },
  ],
  getRowKey: (data) => data.id,
  isSortable: true,
};

export const COMPANY_DOCS_TABLE_CONFIG: TableConfiguration<CompanyFilingsDto> = {
  columns: [
    {
      dataKey: 'type',
      header: 'Document type',
      width: amountColumnWidth,
      cellRenderer: CompanyDocumentTypeRenderer,
      filterRenderer: FilingsTypeFilterRenderer,
    },
    {
      dataKey: 'uploadedFile.originalFilename',
      header: 'File',
      cellRenderer: CompanyFilingsFileNameRenderer,
      filterRenderer: StringFilterRenderer,
    },
    {
      dataKey: 'date',
      header: 'Date',
      width: dateColumnWidth,
      cellRenderer: DocumentDateRenderer,
      filterRenderer: DateFilterRenderer,
    },
  ],
  getRowKey: (data) => data.guid,
  isSortable: true,
  isFilterable: true,
};

const onDateRegex =
  /\son (January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}, \d{4}$/i;

export const removeDateFromDocumentTitle = (title = ''): string => {
  return title.replace(onDateRegex, '').split(', dated')[0];
};

export const getArticleId = (document: SearchDocumentsDto): number => document.report_id + BASE_ARTICLE_ID;

export const getSubTabName = (urlString: string): string => {
  const lastSegmentOfUrl = urlString.split('/').filter(Boolean).pop() || '';
  const tabName = documentSubTabs[lastSegmentOfUrl] || '';
  return tabName;
};

export const documentSubTabs = {
  loans: 'Loan Covenants',
  bonds: 'Bond Covenants',
  filings: 'Documents/Filings',
};
